import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from './axiosBaseQuery';
import type {
  EnrichmentUploadsListResponse,
  EnrichmentUploadStats,
  EnrichmentRowsPage,
  EnrichmentSimilarRowsPage,
  StartSimilarEnrichmentRequest,
  StartSimilarEnrichmentResponse,
} from '@/lib/types/enrichment.types';

/**
 * CSV enrichment API.
 *
 * Talks to the real Express middleware at `/api/enrichment/*`, which resolves
 * the user from the session cookie and forwards to the standalone Enrichment
 * service — no user id is sent from the browser. Uploading a file and
 * starting processing go through `/api/uploads` instead (see uploadsApi.ts) —
 * this slice is left with the read-only endpoints for an already-dispatched
 * batch.
 *
 * Routed through `axiosBaseQuery` so these calls share the session handling
 * in lib/axios (token refresh, disabled-account sign-out).
 */
export const enrichmentApi = createApi({
  reducerPath: 'enrichmentApi',
  baseQuery: axiosBaseQuery('/api/enrichment'),
  tagTypes: ['EnrichmentUploads', 'EnrichmentStats'],
  refetchOnFocus: false,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    /**
     * Every upload the signed-in user has kicked off, newest first. Backs both
     * the job list and the progress screen, and is re-read on each
     * enrichment.progress push rather than on a timer — see sidebar.tsx.
     */
    getEnrichmentUploads: builder.query<EnrichmentUploadsListResponse, void>({
      query: () => '/uploads',
      providesTags: ['EnrichmentUploads'],
      keepUnusedDataFor: 0,
    }),

    /**
     * Live status/counts for a single upload, including the find-similar
     * counters (`similar_*`). Re-read on each enrichment.progress push, which
     * invalidates it by stats_id — see sidebar.tsx.
     */
    getEnrichmentUploadStats: builder.query<EnrichmentUploadStats, string>({
      query: (statsId) => `/stats/${statsId}`,
      providesTags: (_result, _error, statsId) => [{ type: 'EnrichmentStats', id: statsId }],
    }),

    /** Paginated per-row results for a single upload — status and, once processed, match/enriched data for each row. */
    getEnrichmentRows: builder.query<EnrichmentRowsPage, { statsId: string; pageNo?: number; pageSize?: number }>({
      query: ({ statsId, pageNo = 1, pageSize = 25 }) => ({
        url: `/rows/${statsId}`,
        params: { page_no: pageNo, page_size: pageSize },
      }),
    }),

    /**
     * The rows a find-similar run produced, paginated.
     *
     * One endpoint for both types — upstream writes similar companies into the
     * same collection as similar contacts, which is why its route is called
     * `similarcontacts` but serves either.
     *
     * Tagged with the upload's EnrichmentStats id so it refetches on the same
     * pushes that move the `similar_*` counters: a contact row appears here as
     * 'inworkflow' and flips to 'completed' later, and that flip is exactly
     * what `similar_processed` counts.
     */
    getSimilarEnrichmentRows: builder.query<
      EnrichmentSimilarRowsPage,
      { statsId: string; pageNo?: number; pageSize?: number }
    >({
      query: ({ statsId, pageNo = 1, pageSize = 25 }) => ({
        url: `/similar/${statsId}`,
        params: { page_no: pageNo, page_size: pageSize },
      }),
      providesTags: (_result, _error, { statsId }) => [{ type: 'EnrichmentStats', id: statsId }],
    }),

    /**
     * Kicks off a find-similar run for an already-enriched upload.
     *
     * The response is only an ack (`processing_started`) — it carries no rows
     * and no counts, because the service queues the work as a background task
     * and answers immediately. Invalidating the upload's stats starts the
     * caller's poll on the `similar_*` counters straight away rather than
     * leaving it to wait out a full polling interval.
     */
    startSimilarEnrichment: builder.mutation<StartSimilarEnrichmentResponse, StartSimilarEnrichmentRequest>({
      query: (body) => ({
        url: '/similar',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { statsId }) => [{ type: 'EnrichmentStats', id: statsId }],
    }),

    /** Soft-deletes a CSV upload's stats record. */
    deleteEnrichmentCsv: builder.mutation<{ stats_id: string; is_deleted: boolean }, string>({
      query: (statsId) => ({
        url: `/delete/${statsId}`,
        method: 'GET',
      }),
      invalidatesTags: ['EnrichmentUploads'],
    }),

    /**
     * Pushes this batch's csv_enriched rows into the connected CRM. Unlike
     * Workspace's export, there is no mappingtype to send — the enrichment
     * service already picks the field mapping in code by each row's own
     * contact/company type — so the browser only names the CRM and,
     * optionally, the list to enroll the pushed records into. `segmentId` is
     * sent through as-is (including '' for "no list picked"); the Express
     * route collapses it to null upstream.
     */
    exportEnrichmentToCrm: builder.mutation<unknown, { statsId: string; source: string; segmentId?: string | null }>({
      query: ({ statsId, source, segmentId }) => ({
        url: `/export-crm/${statsId}`,
        method: 'POST',
        body: { source, segmentId: segmentId ?? null },
      }),
    }),

    /**
     * Pulls a CRM segment's records into a new enrichment upload and runs it.
     * The service maps the fields itself by `type` and matches inline, so this
     * resolves only once the run is finished (`status: "completed"`) and the
     * id is ready for the progress/report screen.
     */
    importEnrichmentFromCrm: builder.mutation<
      { userupload_id: string; status: string },
      {
        segmentId: string;
        source: string;
        type: 'contact' | 'company';
        filename: string;
        limit: number;
      }
    >({
      query: (body) => ({
        url: '/import-crm',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['EnrichmentUploads'],
    }),
  }),
});

export const {
  useGetEnrichmentUploadsQuery,
  useGetEnrichmentUploadStatsQuery,
  useGetEnrichmentRowsQuery,
  useGetSimilarEnrichmentRowsQuery,
  useDeleteEnrichmentCsvMutation,
  useStartSimilarEnrichmentMutation,
  useExportEnrichmentToCrmMutation,
  useImportEnrichmentFromCrmMutation,
} = enrichmentApi;

export default enrichmentApi;
